import React, { useState, useEffect, useCallback } from 'react';
import { DrillImageMetadata } from './Model';
import { restClient } from './RestClient';

interface DrillImageGalleryProps {
  drillId: number;
  images: DrillImageMetadata[];
  mode: 'compact' | 'full';
}

export const DrillImageGallery: React.FC<DrillImageGalleryProps> = ({ drillId, images, mode }) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (lightboxIndex === null) return;
    if (e.key === 'Escape') setLightboxIndex(null);
    if (e.key === 'ArrowRight') setLightboxIndex(i => i !== null ? Math.min(i + 1, images.length - 1) : null);
    if (e.key === 'ArrowLeft') setLightboxIndex(i => i !== null ? Math.max(i - 1, 0) : null);
  }, [lightboxIndex, images.length]);

  useEffect(() => {
    if (lightboxIndex !== null) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    }
  }, [lightboxIndex, handleKeyDown]);

  if (images.length === 0) return null;

  if (mode === 'compact') {
    const firstImage = images[0];
    return (
      <div className="drill-image-compact">
        <button
          type="button"
          className="drill-image-thumbnail-btn"
          onClick={() => setLightboxIndex(0)}
          aria-label={`View image ${firstImage.fileName}`}
        >
          <img
            src={restClient.getDrillImageUrl(drillId, firstImage.id)}
            alt={firstImage.fileName}
            className="drill-image-thumbnail"
          />
        </button>
        {images.length > 1 && (
          <span className="drill-image-count-badge">+{images.length - 1}</span>
        )}
        {lightboxIndex !== null && (
          <Lightbox
            drillId={drillId}
            images={images}
            currentIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onPrev={() => setLightboxIndex(i => i !== null ? Math.max(i - 1, 0) : null)}
            onNext={() => setLightboxIndex(i => i !== null ? Math.min(i + 1, images.length - 1) : null)}
          />
        )}
      </div>
    );
  }

  // Full mode: thumbnail grid
  return (
    <div className="drill-image-gallery">
      <div className="drill-image-grid">
        {images.map((img, idx) => (
          <button
            key={img.id}
            type="button"
            className="drill-image-thumbnail-btn"
            onClick={() => setLightboxIndex(idx)}
            aria-label={`View image ${img.fileName}`}
          >
            <img
              src={restClient.getDrillImageUrl(drillId, img.id)}
              alt={img.fileName}
              className="drill-image-thumbnail"
            />
          </button>
        ))}
      </div>
      {lightboxIndex !== null && (
        <Lightbox
          drillId={drillId}
          images={images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex(i => i !== null ? Math.max(i - 1, 0) : null)}
          onNext={() => setLightboxIndex(i => i !== null ? Math.min(i + 1, images.length - 1) : null)}
        />
      )}
    </div>
  );
};

// Lightbox sub-component
interface LightboxProps {
  drillId: number;
  images: DrillImageMetadata[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ drillId, images, currentIndex, onClose, onPrev, onNext }) => {
  const current = images[currentIndex];
  return (
    <div className="drill-image-lightbox" onClick={onClose}>
      <div className="drill-image-lightbox-content" onClick={e => e.stopPropagation()}>
        <button type="button" className="drill-image-lightbox-close" onClick={onClose} aria-label="Close">
          &times;
        </button>
        {currentIndex > 0 && (
          <button type="button" className="drill-image-lightbox-nav drill-image-lightbox-prev" onClick={onPrev} aria-label="Previous image">
            &#8249;
          </button>
        )}
        <img
          src={restClient.getDrillImageUrl(drillId, current.id)}
          alt={current.fileName}
          className="drill-image-lightbox-img"
        />
        {currentIndex < images.length - 1 && (
          <button type="button" className="drill-image-lightbox-nav drill-image-lightbox-next" onClick={onNext} aria-label="Next image">
            &#8250;
          </button>
        )}
        <div className="drill-image-lightbox-counter">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  );
};
